import {
  ERROR_MESSAGES,
  ERROR_TYPE,
  POPUP_TYPES,
} from '@/features/Popup/constants';
import { generatePopupConfig } from '@/features/Popup/helpers/generatePopupConfig';
import type { PopupTypes } from '@/features/Popup/types';
import {
  getJackpotModeParam,
  resolveJackpotMode,
} from '@/features/SWJ/helpers/resolveJackpotMode';
import { runJackpotGlsReconcileAfterPrewarmContext } from '@/game-client/jackpot-gls-reconcile-defer';
import type {
  FetchGlsGame,
  JackpotGlsReconcileOptions,
} from '@/game-client/jackpot-gls-reconcile-run';
import { useStore } from '@/store/useStore';
import { COUNTRY_ABBREVIATION } from '@/utilities/constants/account';
import { isExperimentInTreatment } from '@/utilities/getters/getAmplitudeExperiment';
import { getCountry } from '@/utilities/getters/accountGetters';

export const isCanadaLaunch = (): boolean =>
  getCountry(process.env.COUNTRY ?? '') === COUNTRY_ABBREVIATION.CANADA;

type ShowLaunchPopup = (
  data: ReturnType<typeof generatePopupConfig>,
  type: PopupTypes,
) => void;

/** Canada: aggregation request failed after settlement (independent of jackpot reconcile). */
export const shouldShowCanadaLaunchAggregationFailure = (
  aggregationSucceeded: boolean,
): boolean => isCanadaLaunch() && !aggregationSucceeded;

export const showJackpotLaunchAggregationFailurePopup = (
  showPopup: ShowLaunchPopup,
): void => {
  const generic = ERROR_MESSAGES[ERROR_TYPE.GENERIC].main;
  showPopup(
    generatePopupConfig(ERROR_TYPE.GENERAL_ERROR, {
      errorConfig: {
        errorType: ERROR_TYPE.GENERIC,
        errorMessage: generic.popupMessage,
      },
      customConfig: { props: { popupName: generic.popupName } },
    }),
    POPUP_TYPES.PROVIDER,
  );
};

const readJackpotLaunchInputs = (ignoreUrlParam: boolean = false) => {
  const { gameContext, swj, commonUIContext, amplitudeExperiments } =
    useStore.getState();

  return {
    gameContext,
    swj,
    commonUIContext,
    amplitudeExperiments,
    jackpotModeParam: ignoreUrlParam ? null : getJackpotModeParam(),
    canRenderJackpotModule: Boolean(swj.jackpotData?.provider?.uid),
    swjEnabled: swj.jackpotData?.enabled ?? false,
    swjFlagEnabled: swj.isSwjFlagEnabled,
    swjExperimentEnabled: isExperimentInTreatment(
      amplitudeExperiments,
      'site-wide-jackpots',
    ),
    showGameWindow: commonUIContext.showGameWindow ?? false,
  };
};

/** Matches TopBarModule render guard (`jackpotData?.provider?.uid`). */
export const isJackpotModuleRenderable = (): boolean =>
  readJackpotLaunchInputs().canRenderJackpotModule;

/** Resolved jackpotMode for the GLS payload (`undefined` = omit before aggregation). */
export const computeLaunchJackpotMode = (
  ignoreUrlParam: boolean = false,
): boolean | undefined => {
  const { jackpotModeParam, gameContext, swj, ...resolveInput } =
    readJackpotLaunchInputs(ignoreUrlParam);

  if (swj.isJackpotModeSuppressed) {
    return false;
  }

  if (jackpotModeParam === null && !gameContext.gameUid) {
    return undefined;
  }

  return resolveJackpotMode({
    jackpotModeParam,
    ...resolveInput,
    isLaunch: true,
  });
};

export const setJackpotModeUrlParam = (jackpotMode: boolean): void => {
  const url = new URL(window.location.href);
  url.searchParams.set('jackpotMode', String(jackpotMode));
  window.history.replaceState(window.history.state, '', url);
};

export const CONTINUE_WITHOUT_JACKPOTS_RECONCILE_OPTIONS = {
  shouldResetLoadingScreen: true,
  launchVitals: 'abortAndRestart',
} as const satisfies JackpotGlsReconcileOptions;

/** URL opt-out + deferred reconcile after the first initialize URL context ack. */
export const continueWithoutJackpots = (
  fetchGame: FetchGlsGame,
  gameUid: string | null,
  fireGameExit?: () => void,
): void => {
  setJackpotModeUrlParam(false);
  fireGameExit?.();
  runJackpotGlsReconcileAfterPrewarmContext(
    fetchGame,
    gameUid,
    CONTINUE_WITHOUT_JACKPOTS_RECONCILE_OPTIONS,
  );
};

export const isContinueWithoutJackpotsRequested = (): boolean =>
  getJackpotModeParam() === 'false';

/** GLS launched with jackpots but the Top Module cannot render (prewarm / reconcile scenario). */
export const shouldDeliverJackpotReconPrewarm = ({
  glsResponseJackpotMode,
  canRenderJackpotModule = isJackpotModuleRenderable(),
}: {
  glsResponseJackpotMode: boolean | undefined;
  canRenderJackpotModule?: boolean;
}): boolean => glsResponseJackpotMode === true && !canRenderJackpotModule;

export const shouldReconcileJackpotMode = ({
  glsResponseJackpotMode,
  aggregationSettled,
  aggregationSucceeded,
  canRenderJackpotModule = isJackpotModuleRenderable(),
}: {
  glsResponseJackpotMode: boolean | undefined;
  aggregationSettled: boolean;
  /** When false on Canada builds, auto jackpot reconcile is blocked (error UI instead). */
  aggregationSucceeded?: boolean;
  canRenderJackpotModule?: boolean;
}): boolean => {
  if (
    !shouldDeliverJackpotReconPrewarm({
      glsResponseJackpotMode,
      canRenderJackpotModule,
    })
  ) {
    return false;
  }

  if (!aggregationSettled) {
    return false;
  }

  // Non-Canada: still auto-reconcile when aggregation failed (no SWJ / Top Module).
  if (isCanadaLaunch() && aggregationSucceeded === false) {
    return false;
  }

  return true;
};
